import { Avatar, Button, Col, Icon, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchUsers, TableComponents } from 'screens/Admin/components';
import GloryBoardAdjustmentHistory from './GloryBoardAdjustmentHistory';
import GloryBoardPointAdjustment from './GloryBoardPointAdjustment';
import { isOnThisBoard, participantFromBoardRow, participantFromSearch } from './pointAdjustment';
import RewardHistoryModal from './RewardHistoryModal';

const RankChange = ({ change }) => {
  if (change > 0)
    return (
      <Row center>
        <Text col={theme.GREEN} b2 med marg="0 4px 0 0">
          +{change}
        </Text>
        <Icon type="arrowUp" dimensions={10} />
      </Row>
    );
  if (change < 0)
    return (
      <Row center>
        <Text col={theme.RED} b2 med marg="0 4px 0 0">
          {change}
        </Text>
        <Icon type="arrowDown" dimensions={10} />
      </Row>
    );
  return (
    <Text col={theme.GREY_60} b2 med>
      -
    </Text>
  );
};

const BandBadge = ({ band }) => {
  let color = theme.GREY_60;
  let text = 'Rising Supporters';
  let bg = theme.GREY_10;

  switch (band) {
    case 'ELITE':
      color = theme.PRIMARY_WHITE;
      bg = theme.PURPLE;
      text = 'Elite';
      break;
    case 'TOP_10':
      color = theme.PRIMARY_WHITE;
      bg = theme.SECONDARY;
      text = 'Top 10%';
      break;
    case 'TOP_25':
      color = theme.PRIMARY_WHITE;
      bg = theme.ORANGE;
      text = 'Top 25%';
      break;
    case 'TOP_50':
      color = theme.GREY_90;
      bg = theme.TALE;
      text = 'Top 50%';
      break;
    case 'RISING_SUPPORTERS':
    default:
      color = theme.GREY_60;
      bg = theme.GREY_10;
      text = 'Rising Supporters';
      break;
  }

  return (
    <div
      style={{
        backgroundColor: bg,
        padding: '4px 8px',
        borderRadius: '12px',
        display: 'inline-block'
      }}
    >
      <Text col={color} tiny med>
        {text}
      </Text>
    </div>
  );
};

/**
 * Who is being worked on, held across the whole width above the two working columns.
 *
 * It is the one thing both columns are about, so it is deliberately not inside either of them: a
 * balance shown in the left card would read as something the form had computed.
 */
const SelectedParticipant = ({ participant, teamName, balance, onViewHistory, onClear }) => (
  <Col
    noFlex
    wid="100%"
    pad="16px"
    marg="20px 0 0"
    bg={theme.GREY_10}
    hasRadius="14px"
    style={{ border: `1px solid ${theme.GREY_20}`, boxSizing: 'border-box' }}
  >
    <Row between center wid="100%" gap="16px">
      <Row center noFlex minWid="0">
        <Avatar user={participant} avatar={participant.avatar} tiny />
        <Col marg="0 0 0 12px" noFlex minWid="0">
          <Text med>{participant.name}</Text>
          <Text c1 col={theme.GREY_60}>
            @{participant.username} · {teamName}
          </Text>
        </Col>
      </Row>

      <Row center noFlex gap="16px">
        <Col noFlex endAll>
          <Text c1 col={theme.GREY_60}>
            Current Points
          </Text>

          {balance.status === 'loading' && <Spinner2 col={theme.SECONDARY} />}

          {balance.status === 'error' && (
            <Text med col={theme.RED}>
              Could not be loaded
            </Text>
          )}

          {balance.status === 'ready' && <Text h3>{balance.totalPoints.toLocaleString()}</Text>}
        </Col>

        <Button
          text="View reward history"
          onClick={onViewHistory}
          bordered
          small
          style={{ width: 'fit-content' }}
        />

        <Button
          text="Clear"
          onClick={onClear}
          bordered
          small
          style={{ width: 'fit-content' }}
        />
      </Row>
    </Row>

    {balance.status === 'ready' && !balance.onThisBoard && (
      <Text c1 col={theme.GREY_60} marg="12px 0 0">
        This fan is not yet on this board — they have no points with {teamName}.
      </Text>
    )}
  </Col>
);

const IDLE_BALANCE = { status: 'idle', totalPoints: 0, badge: null, onThisBoard: true };

const GloryBoardPanel = ({ team }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [balance, setBalance] = useState(IDLE_BALANCE);
  // Bumped after a successful adjustment so the history refetches and shows the one just made.
  const [adjustmentsToken, setAdjustmentsToken] = useState(0);

  // Who is selected lives in the URL alongside the team and the tab, so the link an administrator
  // sends a colleague opens on the same fan rather than on an empty search box.
  const [searchParams, setSearchParams] = useSearchParams();
  const participantParam = searchParams.get('participant') || '';

  const setParticipantParam = (userId) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (userId) next.set('participant', userId);
        else next.delete('participant');
        return next;
      },
      { replace: true }
    );

  const teamName = team?.name || team?.username || 'this team';

  /**
   * The balance is fetched for the (team, fan) pair rather than read off the board row, so a fan
   * found by search — who has no row — reads the same number as one picked from the ranking.
   * `limit=1` because only `totalPoints` is consumed; the ledger page is discarded.
   */
  const selectParticipant = async (next) => {
    setParticipant(next);
    setBalance({ ...IDLE_BALANCE, status: 'loading' });

    try {
      const res = await Api.get(
        `/admin/rewardHistories/list?teamId=${team._id}&userId=${next.userId}&limit=1`
      );

      if (!res?.data) throw new Error('No balance in response');

      const totalPoints = res.data.totalPoints || 0;

      setBalance({
        status: 'ready',
        totalPoints,
        badge: res.data.badge || null,
        onThisBoard: isOnThisBoard(next, totalPoints)
      });
    } catch (e) {
      console.error(e);
      setBalance({ ...IDLE_BALANCE, status: 'error' });
    }
  };

  const pickParticipant = (next) => {
    selectParticipant(next);
    setParticipantParam(next.userId);
  };

  const clearParticipant = () => {
    setParticipant(null);
    setBalance(IDLE_BALANCE);
    setParticipantParam(null);
  };

  /**
   * After an adjustment lands, re-read the balance the server now holds.
   *
   * Deliberately without flipping the status back to `loading`: that would unmount the adjustment
   * form and take its success message with it. The figure is refetched rather than decremented
   * locally because the server's is the only one that is true.
   */
  const refreshAfterAdjustment = async () => {
    setAdjustmentsToken((token) => token + 1);
    fetchGloryBoard();

    if (!participant) return;

    try {
      const res = await Api.get(
        `/admin/rewardHistories/list?teamId=${team._id}&userId=${participant.userId}&limit=1`
      );

      if (!res?.data) return;

      setBalance((prev) => ({ ...prev, totalPoints: res.data.totalPoints || 0 }));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (team?._id) {
      fetchGloryBoard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team]);

  /**
   * Restore the fan named in the URL — on a shared link, a reload, or the back button.
   *
   * The loaded page of the board is consulted first because its row carries `fromBoard`, which is
   * what tells a genuine zero balance apart from a fan who has no standing on this team at all.
   * Only a fan ranked beyond that page needs the extra lookup.
   */
  useEffect(() => {
    if (!team?._id || loading) return;

    if (!participantParam) {
      if (participant) {
        setParticipant(null);
        setBalance(IDLE_BALANCE);
      }
      return;
    }

    if (participant?.userId === participantParam) return;

    const row = data.find((item) => item.userId === participantParam);

    if (row) {
      selectParticipant(participantFromBoardRow(row));
      return;
    }

    let cancelled = false;

    (async () => {
      const res = await Api.post('/admin/getUser', { userId: participantParam }, null, {
        silent: true
      });

      if (cancelled || !res?._id) return;

      selectParticipant(participantFromSearch(res));
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?._id, participantParam, loading, data, participant?.userId]);

  const fetchGloryBoard = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const offset = isLoadMore ? data.length : 0;
      const res = await Api.get(
        `/admin/teams/gloryBoard?teamId=${team._id}&limit=50&offset=${offset}`
      );
      if (res?.data?.ranking) {
        const newRanking = res.data.ranking;
        const totalCount = res.data.total;

        if (isLoadMore) {
          setData((prev) => {
            const existingIds = new Set(prev.map((item) => item._id));
            const uniqueNewRanking = newRanking.filter((item) => !existingIds.has(item._id));
            return [...prev, ...uniqueNewRanking];
          });
        } else {
          setData(newRanking);
        }
        setTotal(totalCount);
        setHasMore(offset + newRanking.length < totalCount);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    setLoadingMore(false);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50 && hasMore && !loading && !loadingMore) {
      fetchGloryBoard(true);
    }
  };

  return (
    <Col pad="24px" pos="relative">
      <Row between center wid="100%" marg="0 0 16px" gap="16px">
        <Col noFlex minWid="0">
          <Text h3>GloryBoard</Text>
          {/*
            The fan-facing board is scoped to the current match window and resets at every
            halftime; this one is not, and deliberately so — an administrator is about to
            subtract from a LIFETIME balance, so the ordering they read before doing it must be
            the figure they can act on. Without this label the two boards simply disagree and
            nothing on screen says why.
          */}
          <Text b2 col={theme.GREY_60} marg="4px 0 0">
            All-time ranking — not the current match window
          </Text>
        </Col>

        <Row center noFlex>
          <Text h4 regular>
            Total users:{' '}
          </Text>
          <Text h4 med marg="0 0 0 5px">
            {total}
          </Text>
        </Row>
      </Row>

      <SearchUsers
        title="Find a participant"
        handleSelect={(user) => pickParticipant(participantFromSearch(user))}
        noRounded
        wid="100%"
      />

      {participant ? (
        <>
          <SelectedParticipant
            participant={participant}
            teamName={teamName}
            balance={balance}
            onViewHistory={() => setSelectedUser(participant)}
            onClear={clearParticipant}
          />

          {/*
            The form and the record of what has already been done to this balance are two halves of
            one decision, so they sit side by side rather than one below the other — the history was
            previously far enough down the page to be read after the adjustment instead of before
            it. The columns wrap on a narrow panel rather than crushing the reason dropdown.
          */}
          <Row start noFlex wid="100%" gap="20px" marg="20px 0 0" alignItems="stretch" rowWrap>
            {balance.status === 'ready' && (
              <Col minWid="320px" stretch style={{ flex: '1 1 380px' }}>
                <GloryBoardPointAdjustment
                  participant={participant}
                  teamId={team._id}
                  teamName={teamName}
                  totalPoints={balance.totalPoints}
                  onApplied={refreshAfterAdjustment}
                />
              </Col>
            )}

            <Col minWid="320px" stretch style={{ flex: '1 1 380px' }}>
              <GloryBoardAdjustmentHistory
                teamId={team._id}
                userId={participant.userId}
                refreshToken={adjustmentsToken}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Text col={theme.GREY_60} marg="8px 0 0">
          Search for a participant, or pick one from the board below.
        </Text>
      )}

      <Text h4 bold marg="28px 0 8px">
        Ranking
      </Text>

      <div style={{ height: '40vh', overflowY: 'auto' }} onScroll={handleScroll}>
        <TableComponents.List
          loading={loading}
          Header={() => (
            <React.Fragment>
              <TableComponents.Th first>
                <Text>Rank</Text>
              </TableComponents.Th>
              <TableComponents.Th>
                <Text>User</Text>
              </TableComponents.Th>
              <TableComponents.Th>
                <Text>Band</Text>
              </TableComponents.Th>
              <TableComponents.Th>
                <Text>Reward Points</Text>
              </TableComponents.Th>
              <TableComponents.Th>
                <Text>Rank Change</Text>
              </TableComponents.Th>
              <TableComponents.Th last>
                <Text>Previous Rank</Text>
              </TableComponents.Th>
            </React.Fragment>
          )}
          Items={() =>
            data.map((item, i) => (
              <TableComponents.Tr
                key={item._id || i}
                onClick={() => pickParticipant(participantFromBoardRow(item))}
                style={{
                  cursor: 'pointer',
                  backgroundColor: participant?.userId === item.userId ? theme.GREY_10 : undefined
                }}
              >
                <TableComponents.Td first>
                  <Text>{item.currentRank}</Text>
                </TableComponents.Td>
                <TableComponents.Td>
                  <Row center>
                    <Avatar user={item} avatar={item.avatar} tiny />
                    <Col marg="0 0 0 12px">
                      <Text med>{item.name}</Text>
                      <Text tiny col={theme.GREY_60}>
                        @{item.username}
                      </Text>
                    </Col>
                  </Row>
                </TableComponents.Td>
                <TableComponents.Td>
                  <BandBadge band={item.band} />
                </TableComponents.Td>
                <TableComponents.Td>
                  <Text>{item.rewardPoint}</Text>
                </TableComponents.Td>
                <TableComponents.Td>
                  <RankChange change={item.rankChange} />
                </TableComponents.Td>
                <TableComponents.Td last>
                  <Text>{item.previousRank || '-'}</Text>
                </TableComponents.Td>
              </TableComponents.Tr>
            ))
          }
        />
        {loadingMore && (
          <Row centerAll marg="10px 0">
            <Text>Loading more...</Text>
          </Row>
        )}
      </div>

      {selectedUser && (
        <RewardHistoryModal
          teamId={team._id}
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </Col>
  );
};

export default GloryBoardPanel;
